/* ==========================================================================
   INTERACTIVE AFRICA MAP MODULE (Using africa (1).svg)
   ========================================================================== */

const countryData = {
  SO: {
    name: "Somalia & Somaliland",
    region: "Horn of Africa",
    type: "Primary Operational Hub",
    focus: "Public Health Systems, PFM Advisory & Micro-Enterprise Assistance",
    projects: "Health Management Information System (HMIS) rollout, Local Government Capacity Building, Agri-Business Advisory.",
    badge: "Primary Hub"
  },
  KE: {
    name: "Kenya",
    region: "East Africa",
    type: "Regional Operational Hub",
    focus: "GIS & Spatial Analysis, Watershed Management, Trade Facilitation",
    projects: "Regional Trade Corridor Logistics Assessment, Spatial Land Surveys, SME Advisory.",
    badge: "Regional Hub"
  },
  ET: {
    name: "Ethiopia",
    region: "East Africa",
    type: "Project Footprint",
    focus: "Agri-Business & Technical Surveys",
    projects: "Landscape Assessment, Climate-Smart Agriculture Capacity Building.",
    badge: "Active Engagement"
  },
  UG: {
    name: "Uganda",
    region: "East Africa",
    type: "Project Footprint",
    focus: "Grant & Financial Management Advisory",
    projects: "Multi-sector M&E systems, Financial Audit & Compliance Support.",
    badge: "Active Engagement"
  },
  RW: {
    name: "Rwanda",
    region: "East Africa",
    type: "Project Footprint",
    focus: "Governance & Institutional Strengthening",
    projects: "Shared Services Center Analysis, Institutional Oversight.",
    badge: "Active Engagement"
  },
  DJ: {
    name: "Djibouti",
    region: "Horn of Africa",
    type: "Project Footprint",
    focus: "Trade Facilitation & Border-Post Advisory",
    projects: "Border Post Management & Trade Corridor Efficiency.",
    badge: "Active Engagement"
  },
  TZ: {
    name: "Tanzania",
    region: "East Africa",
    type: "Project Footprint",
    focus: "Resource Management & M&E Systems",
    projects: "Regional Supply Chain & Resource Logistics Assessment.",
    badge: "Active Engagement"
  },
  NG: {
    name: "Nigeria",
    region: "West Africa",
    type: "Strategic Advisory Footprint",
    focus: "SME Support & Trade Facilitation",
    projects: "SME Innovation & Enterprise Support Frameworks.",
    badge: "Strategic Advisory"
  },
  ZA: {
    name: "South Africa",
    region: "Southern Africa",
    type: "Strategic Partner Network",
    focus: "Institutional Advisory & Capacity Building",
    projects: "Cross-border Financial Governance Research.",
    badge: "Partner Network"
  }
};

// Fallback metadata for non-presence countries to allow full map inspection
const defaultCountryData = {
  region: "Pan-African Region",
  type: "African Continent",
  focus: "Advisory & Consultancy Services Capacity",
  projects: "Hope Base Advisory network capability across Sub-Saharan Africa.",
  badge: "Continent Wide"
};

export async function initAfricaMap() {
  const container = document.querySelector('.africa-svg-container');
  const countryNameEl = document.getElementById('map-country-name');
  const countryRegionEl = document.getElementById('map-country-region');
  const countryFocusEl = document.getElementById('map-country-focus');
  const countryProjectsEl = document.getElementById('map-country-projects');
  const countryStatusEl = document.getElementById('map-country-status');
  const countryBadgeEl = document.getElementById('map-country-badge');
  const tooltipEl = document.getElementById('map-tooltip');

  if (!container || !countryNameEl) return;

  try {
    // Load local africa.svg file (replaced with africa (1).svg)
    const response = await fetch('/images/africa.svg');
    if (!response.ok) throw new Error('Failed to load africa.svg');
    const svgText = await response.text();
    
    // Inject SVG into container
    container.innerHTML = svgText;

    const svg = container.querySelector('svg');
    if (svg) {
      svg.setAttribute('width', '100%');
      svg.setAttribute('height', '100%');
      if (!svg.getAttribute('viewBox')) {
        svg.setAttribute('viewBox', '0 0 1000 1001');
      }
    }

    const paths = container.querySelectorAll('path');
    
    // Style and categorize paths
    paths.forEach(path => {
      // Clear inline fill style so CSS tokens take effect
      path.style.fill = '';
      path.style.stroke = '';

      const code = path.getAttribute('id') || path.getAttribute('data-id');
      const name = path.getAttribute('data-name') || path.getAttribute('title') || code;
      
      path.classList.add('map-path');

      if (code && countryData[code]) {
        path.classList.add('presence');
        if (code === 'SO') path.classList.add('active');
      }

      // Mouse enter event
      path.addEventListener('mouseenter', (e) => {
        paths.forEach(p => p.classList.remove('hover-highlight'));
        path.classList.add('hover-highlight');

        if (tooltipEl) {
          tooltipEl.textContent = name;
          tooltipEl.style.display = 'block';
          tooltipEl.style.left = `${e.clientX + 12}px`;
          tooltipEl.style.top = `${e.clientY - 28}px`;
        }

        updateCard(code, name);
      });

      path.addEventListener('mousemove', (e) => {
        if (tooltipEl) {
          tooltipEl.style.left = `${e.clientX + 12}px`;
          tooltipEl.style.top = `${e.clientY - 28}px`;
        }
      });

      path.addEventListener('mouseleave', () => {
        path.classList.remove('hover-highlight');
        if (tooltipEl) tooltipEl.style.display = 'none';
      });

      path.addEventListener('click', () => {
        selectCountry(code, name);
      });
    });

    // Country pill selectors beneath the map
    const pills = document.querySelectorAll('.map-country-pill');
    pills.forEach(pill => {
      pill.addEventListener('click', () => {
        const code = pill.dataset.country;
        pills.forEach(p => p.classList.remove('active'));
        pill.classList.add('active');
        
        const targetPath = container.querySelector(`path#${code}`);
        const name = targetPath ? (targetPath.getAttribute('data-name') || code) : code;
        selectCountry(code, name);
      });
    });

    // Function to update the right side card details
    function updateCard(code, fallbackName) {
      const data = (code && countryData[code]) || {
        name: fallbackName || code,
        ...defaultCountryData
      };

      countryNameEl.textContent = data.name;
      if (countryRegionEl) countryRegionEl.textContent = data.region;
      if (countryFocusEl) countryFocusEl.textContent = data.focus;
      if (countryProjectsEl) countryProjectsEl.textContent = data.projects;
      if (countryStatusEl) countryStatusEl.textContent = data.type;
      if (countryBadgeEl) countryBadgeEl.textContent = data.badge;
    }

    function selectCountry(code, fallbackName) {
      paths.forEach(p => p.classList.remove('active'));
      if (code) {
        const targetPath = container.querySelector(`path#${code}`);
        if (targetPath) {
          targetPath.classList.add('active');
        }
      }

      // Sync active state with country pills
      pills.forEach(p => {
        if (p.dataset.country === code) {
          p.classList.add('active');
        } else {
          p.classList.remove('active');
        }
      });

      updateCard(code, fallbackName);
    }

    // Set initial card data for primary hub (Somalia)
    updateCard('SO');

  } catch (err) {
    console.error('Error initializing africa.svg:', err);
  }
}
